<template>
  <div class="container application-detail">
    <!-- 头部 -->
    <header class="header">
      贷款申请详情 - 申请编号：{{ applyNoDisplay }}
    </header>

    <!-- 主内容区域 -->
    <main class="main-content">
      <!-- 产品信息 -->
      <section>
        <h2 class="section-title">产品信息</h2>
        <hr class="section-divider" />

        <div class="info-box">
          <h3 class="section-title">产品信息</h3>
          <div class="product-info">
            <div>
              <div class="info-item">
                <span class="label">产品名称：</span>
                <span class="value">{{ historyList.title || "--" }}</span>
              </div>
              <div class="info-item">
                <span class="label">所属银行：</span>
                <span class="value">{{ historyList.departName || "--" }}</span>
              </div>
            </div>
            <div>
              <div class="info-item">
                <span class="label">利率范围：</span>
                <span class="value">{{ rateRangeDisplay }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 申请人信息 -->
      <section>
        <h2 class="section-title">申请人信息</h2>
        <hr class="section-divider" />

        <div class="info-box">
          <h3 class="section-title">申请人信息（已确认）</h3>
          <div class="applicant-info">
            <div class="contact-info">
              联系人姓名：{{
                applicantInfo.contactName || applicantInfo.name || "--"
              }}
              | 身份证：{{ applicantInfo.contactIdCard || "--" }} | 手机号：{{
                applicantInfo.contactPhone || "--"
              }}
            </div>
            <div class="contact-info" v-if="applicantInfo.legalPersonName">
              法人姓名：{{ applicantInfo.legalPersonName || "--" }} | 身份证：{{
                applicantInfo.legalPersonIdCard || "--"
              }}
              | 手机号：{{ applicantInfo.legalPersonPhone || "--" }}
            </div>

            <!-- 附件预览（身份证、人像、营业执照） -->
            <div class="attachment-container">
              <span class="label" v-if="identityObj?.identityTypeCode == '4'"
                >法人身份证附件：</span
              >
              <span class="label" v-else>身份证附件：</span>
              <el-image
                v-if="idFrontUrl"
                class="attachment-thumb"
                :src="idFrontUrl"
                :preview-src-list="attachmentPreviewUrls"
                fit="cover"
              >
                <template #error>
                  <div class="image-slot">
                    <i class="el-icon-picture-outline"></i>
                  </div>
                </template>
              </el-image>
              <el-image
                v-if="idBackUrl"
                class="attachment-thumb"
                :src="idBackUrl"
                :preview-src-list="attachmentPreviewUrls"
                fit="cover"
              >
                <template #error>
                  <div class="image-slot">
                    <i class="el-icon-picture-outline"></i>
                  </div>
                </template>
              </el-image>
              <span
                class="label"
                style="margin-left: 20px"
                v-if="identityObj?.identityTypeCode == '4'"
                >营业执照附件：</span
              >
              <el-image
                v-if="licenseUrl"
                class="attachment-thumb"
                :src="licenseUrl"
                :preview-src-list="attachmentPreviewUrls"
                fit="cover"
              >
                <template #error>
                  <div class="image-slot">
                    <i class="el-icon-picture-outline"></i>
                  </div>
                </template>
              </el-image>
              <div
                v-if="!idFrontUrl && !idBackUrl && !licenseUrl"
                class="material-item"
              >
                <span class="material-name">暂无附件</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 贷款详情 -->
      <section>
        <div class="info-box">
          <h3 class="section-title">贷款详情</h3>
          <div
            class="loan-details"
            style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px"
          >
            <div class="detail-item">
              <span class="label">申请金额：</span>
              <span class="value">{{ formatWan(historyList.money) }}</span>
            </div>
            <div class="detail-item">
              <span class="label">申请期限：</span>
              <span class="value">{{ historyList.loanTerm || "--" }}个月</span>
            </div>
            <div class="detail-item">
              <span class="label">经营年限：</span>
              <span class="value">{{ historyList.operatYear || "--" }}年</span>
            </div>
            <div class="detail-item">
              <span class="label">年营业额：</span>
              <span class="value">{{ formatWan(historyList.income) }}</span>
            </div>
            <div class="detail-item">
              <span class="label">资金用途：</span>
              <span class="value">{{
                historyList.fundUsageDescription || "--"
              }}</span>
            </div>
            <div class="detail-item">
              <span class="label">申请日期：</span>
              <span class="value">{{ historyList.applyTime || "--" }}</span>
            </div>
            <div class="purpose-item" style="grid-column: 1 / -1">
              <span class="label">用途说明：</span>
              <span class="value">{{ historyList.use || "--" }}</span>
            </div>
          </div>
        </div>
      </section>

      <section v-if="isWarehouseLoan">
        <div class="info-box">
          <h3 class="section-title">质押仓单信息</h3>
          <div
            class="loan-details"
            style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px"
          >
            <!-- 质押仓单：在可选择条件下展示选择控件，否则展示编码并可点击查看 -->
            <div class="detail-item">
              <span class="label">质押仓单：</span>
              <template v-if="canSelectWarehouse">
                <el-select
                  v-model="warehouseSelectId"
                  placeholder="请选择抵押仓单"
                  @change="handleWarehouseReceiptChange"
                  style="flex: 1"
                >
                  <el-option
                    v-for="item in warehouseReceiptOptions"
                    :key="item.id"
                    :value="item.value"
                    :label="item.label"
                  />
                </el-select>
              </template>
              <template v-else>
                <span
                  class="value"
                  :class="{ 'clickable-td': canClickWarehouse }"
                  @click="
                    canClickWarehouse && handleViewContract('关联质押仓单')
                  "
                >
                  {{ warehouseReceiptLabel }}
                </span>
              </template>
            </div>

            <!-- 质押数量：在可选择条件下展示输入框，否则展示数量 -->
            <div class="detail-item">
              <span class="label">质押数量：</span>
              <span class="value"
                >{{ historyList.warehouseNumber || 0 }} KG</span
              >
            </div>
          </div>
        </div>
      </section>

      <!-- 上传材料 -->
      <section>
        <h2 class="section-title">上传材料</h2>
        <hr class="section-divider" />

        <div class="material-grid">
          <template v-for="(item, index) in parsedFiles">
            <el-image
              v-if="isImage(item.imgUrl)"
              :key="`img-${index}`"
              class="material-thumb"
              :src="fileUrl(item.imgUrl)"
              :preview-src-list="materialPreviewUrls"
              fit="cover"
            >
              <template #error>
                <div class="image-slot">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </template>
            </el-image>
            <div v-else :key="`file-${index}`" class="material-item">
              <span class="material-name clickable" @click="openFile(item)"
                >• {{ item.FilesName || "附件" }}</span
              >
            </div>
          </template>
          <div
            v-if="!parsedFiles || parsedFiles.length === 0"
            class="material-item"
          >
            <span class="material-name">暂无附件</span>
          </div>
        </div>
      </section>

      <!-- 审核结果 -->
      <section>
        <h2 class="section-title">审核结果</h2>
        <hr class="section-divider" />

        <div class="audit-result">
          <div class="audit-status">
            <div>
              <span class="label">审核状态：</span>
              <span class="status-indicator">
                <span class="status-approved">{{ statusName }}</span>
              </span>
            </div>
            <div v-if="historyList.approveTime">
              <span class="label">审核时间：</span>
              <span class="value">{{ historyList.approveTime }}</span>
            </div>
          </div>

          <div class="audit-opinion" v-if="historyList.failRemark">
            <span class="label">审核意见：</span>
            <span class="value">{{ historyList.failRemark }}</span>
          </div>
        </div>
      </section>

      <!-- 底部提示 -->
      <div class="bottom-notice">
        <div class="notice-title">温馨提示：</div>
        <div class="notice-content">
          • 审核期间请保持手机畅通，银行可能会电话联系您核实信息
        </div>
      </div>

      <!-- 确定按钮：移动到页面最底部，仅在可选择条件下显示 -->
      <div v-if="canSelectWarehouse" class="bottom-actions">
        <el-button type="primary" @click="confirmWarehouseSelection"
          >确定</el-button
        >
      </div>
    </main>

    <!-- 仓单出入库记录弹窗 -->
    <InoutRecordDialog
      :visible="recordDialogVisible"
      :receipt-id="warehouseReceiptId"
      @update:visible="recordDialogVisible = $event"
    />
  </div>
</template>

<script>
import {
  operateDetails,
  getWarehouseReceiptList,
  initiateLoanApproval,
} from "@/api/RuralFinance";
import InoutRecordDialog from "@/views/memberCenter/warehousingService/warehouseReceiptManagement/InoutRecordDialog.vue";
import { mapGetters } from "vuex";

export default {
  name: "ApplicationDetail",
  components: {
    InoutRecordDialog,
  },
  props: {
    applyId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      // 详情数据
      historyList: {},
      baseUrl: window.globalUrl.HOME_API,
      // 仓单贷选择相关
      warehouseReceiptList: [],
      warehouseSelectId: "",
      warehouseSelectNumber: "",
      balanceWeight: 0,
      // 弹窗控制
      recordDialogVisible: false,
      warehouseReceiptId: "",
      // 状态与类型映射
      statusType: [
        { label: "驳回", value: "-1" },
        { label: "草稿", value: "0" },
        { label: "审批中", value: "1" },
        { label: "通过", value: "2" },
        { label: "待放款", value: "3" },
        { label: "放款中", value: "4" },
        { label: "还款中", value: "5" },
        { label: "还款成功", value: "6" },
        { label: "还款异常", value: "7" },
      ],
      types: [
        { label: "集体经济组织", value: "3" },
        { label: "农业企业", value: "41" },
        { label: "家庭农场", value: "42" },
        { label: "合作社", value: "43" },
      ],
    };
  },
  computed: {
    ...mapGetters(["identityObj", "topSelectAreacode"]),
    isWarehouseLoan() {
      return String(this.historyList?.fundUsage) == "1";
    },
    applicantInfo() {
      const h = this.historyList || {};
      return {
        contactName: h.agentName || h.name || "",
        contactIdCard: h.agentNo || h.idCardNo || "",
        contactPhone: h.contactPhone || h.phone || "",
        legalPersonName: h.legalPersonName || "",
        legalPersonIdCard: h.legalPersonIdCard || h.legalPersonNo || "",
        legalPersonPhone: h.legalPersonPhone || "",
      };
    },
    // 仅在从“申请放款”入口(flag=1)进入时允许展示选择仓单逻辑
    isApplyLoanMode() {
      return String(this.$route?.query?.flag || "") === "1";
    },
    // 仅在：仓单贷、状态不为3、且仓单未选择(-1)时才允许选择与确认
    canSelectWarehouse() {
      const isWL = this.isWarehouseLoan;
      const status = String(this.historyList?.status || "");
      const wid = String(this.historyList?.warehouseId);
      const viaApplyLoan = this.isApplyLoanMode;
      return viaApplyLoan && isWL && status == "3" && wid == "-1";
    },
    warehouseReceiptOptions() {
      return (this.warehouseReceiptList || []).map((item) => ({
        id: item.id,
        label:
          item.number +
          item.grainCategoryLevel +
          item.grainCategoryName +
          " (可用 " +
          (item.balanceWeight || 0) +
          " KG)",
        value: item.id,
      }));
    },
    warehouseReceiptLabel() {
      const code = this.historyList?.warehouseCode;
      const id = this.historyList?.warehouseId;
      if (code) return code;
      if (id === -1 || id === "-1") return "待入库仓单";
      if (id === null || id === undefined || id === "") return "未选择仓单";
      return "--";
    },
    canClickWarehouse() {
      const id = this.historyList?.warehouseId;
      return !(
        id === null ||
        id === undefined ||
        id === "" ||
        id === -1 ||
        id === "-1"
      );
    },
    idFrontUrl() {
      const oss =
        this.historyList?.userCardFrontImg || this.historyList?.bossCardFront;
      return this.fileUrl(oss);
    },
    idBackUrl() {
      const oss =
        this.historyList?.userCardBackImg || this.historyList?.bossCardBack;
      return this.fileUrl(oss);
    },
    licenseUrl() {
      const oss =
        this.historyList?.licenseImg || this.historyList?.businessLicense;
      return this.fileUrl(oss);
    },
    attachmentPreviewUrls() {
      return [this.idFrontUrl, this.idBackUrl, this.licenseUrl].filter(Boolean);
    },
    materialPreviewUrls() {
      const files = this.parsedFiles || [];
      const urls = files
        .map((f) => f && f.imgUrl)
        .filter((p) => this.isImage(p))
        .map((p) => this.fileUrl(p));
      return urls;
    },
    rateRangeDisplay() {
      const min = this.historyList?.minRate;
      const max = this.historyList?.maxRate;
      const hasMin = min !== undefined && min !== null && min !== "";
      const hasMax = max !== undefined && max !== null && max !== "";
      const fmt = (v) => {
        const num = Number(v);
        return Number.isFinite(num) ? num : String(v);
      };
      if (hasMin && hasMax) return `${fmt(min)}% - ${fmt(max)}%`;
      if (hasMin) return `${fmt(min)}%`;
      if (hasMax) return `${fmt(max)}%`;
      return "--";
    },
    parsedFiles() {
      try {
        if (!this.historyList.files) return [];
        const list = JSON.parse(this.historyList.files);
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    },
    statusName() {
      const value = String(this.historyList.status || "");
      const found = this.statusType.find((s) => s.value === value);
      return found ? found.label : "--";
    },
    typesName() {
      const value = String(this.historyList.userType || "");
      const found = this.types.find((t) => t.value === value);
      return found ? found.label : "--";
    },
    isCompany() {
      return (
        String(this.historyList.userType) === "41" ||
        String(this.historyList.userType) === "43" ||
        String(this.historyList.userType) === "3"
      );
    },
    applyNoDisplay() {
      return this.historyList.applicationNumber || "--";
    },
    routeApplyId() {
      return this.$route?.query?.applyId || null;
    },
  },
  created() {
    const id = this.applyId || this.routeApplyId;
    if (id) {
      this.init(String(id));
    }
  },
  methods: {
    fileUrl(oss) {
      if (!oss) return "";
      return `${this.baseUrl}admin/file/get?ossFilePath=${oss}`;
    },
    isImage(ossPath) {
      if (!ossPath || typeof ossPath !== "string") return false;
      return /\.(png|jpe?g|gif|bmp|webp)$/i.test(ossPath);
    },
    formatMoney(val) {
      if (val == null || val === "") return "--";
      const num = Number(val);
      if (Number.isNaN(num)) return String(val);
      // 如果服务端是元，这里直接展示元；如果是万元可自行扩展格式化
      return `${num}${
        this.historyList.money &&
        String(this.historyList.money).includes("万元")
          ? ""
          : "元"
      }`;
    },

    // 将金额换算为万元并友好展示
    formatWan(val) {
      if (val == null || val === "") return "--";
      const num = Number(val);
      if (Number.isNaN(num)) return "--";
      const wan = num / 10000;
      const str = wan
        .toFixed(2)
        .replace(/\.00$/, "")
        .replace(/(\.\d)0$/, "$1");
      return `${str} 万元`;
    },

    async init(applyId) {
      try {
        const params = { applyId };
        const res = await operateDetails(params);
        this.historyList = res?.data || {};
        if (this.canSelectWarehouse) {
          await this.fetchWarehouseReceiptList();
        }
      } catch (error) {
        console.error("获取申请详情失败:", error);
      }
    },

    // 合同查看入口（仅保留质押仓单）
    handleViewContract(contractType) {
      if (contractType === "关联质押仓单") {
        this.handleWarehouseReceipt();
      }
    },

    // 仓单详情
    async handleWarehouseReceipt() {
      if (!this.historyList.warehouseId || this.historyList.warehouseId == -1) {
        this.$message &&
          this.$message.error &&
          this.$message.error("请选择仓单");
        return;
      }
      try {
        this.recordDialogVisible = true;
        await this.$nextTick();
        this.warehouseReceiptId = String(this.historyList.warehouseId || -1);
      } catch (error) {
        console.error("仓单弹窗失败:", error);
        this.recordDialogVisible = false;
      }
    },

    // 拉取仓单列表
    async fetchWarehouseReceiptList() {
      try {
        const res = await getWarehouseReceiptList({});
        if (res && (res.code === 0 || res.code === 200)) {
          this.warehouseReceiptList = res.data || [];
        }
      } catch (e) {
        console.error("获取仓单列表失败:", e);
      }
    },

    // 选择仓单变化
    handleWarehouseReceiptChange(val) {
      const selected = (this.warehouseReceiptList || []).find(
        (item) => item.id === val
      );
      if (selected) {
        this.balanceWeight = selected.balanceWeight || 0;
        this.warehouseSelectNumber = selected.number || "";
      }
    },

    // 确认选择仓单
    async confirmWarehouseSelection() {
      if (!this.canSelectWarehouse) {
        this.$message &&
          this.$message.warning &&
          this.$message.warning("当前状态不可选择仓单");
        return;
      }
      if (!this.warehouseSelectId) {
        this.$message &&
          this.$message.error &&
          this.$message.error("请选择仓单");
        return;
      }
      const applyId = this.applyId || this.routeApplyId;
      try {
        const res = await initiateLoanApproval({
          applyId,
          warehouseId: this.warehouseSelectId,
        });
        if (res && (res.code === 0 || res.code == 200)) {
          this.$message &&
            this.$message.success &&
            this.$message.success(res.msg || "操作成功");
          // 更新本地显示并刷新详情
          this.historyList.warehouseId = this.warehouseSelectId;
          this.historyList.warehouseCode = this.warehouseSelectNumber;
          await this.init(String(applyId));
        } else {
          this.$message &&
            this.$message.error &&
            this.$message.error(res?.msg || "操作失败");
        }
      } catch (e) {
        console.error("提交仓单选择失败:", e);
        this.$message &&
          this.$message.error &&
          this.$message.error("提交失败，请稍后重试");
      }
    },

    // 设计稿的附件预览交互
    previewDocument(type) {
      const map = {
        "id-front":
          this.historyList.userCardFrontImg || this.historyList.bossCardFront,
        "id-back":
          this.historyList.userCardBackImg || this.historyList.bossCardBack,
        license:
          this.historyList.licenseImg || this.historyList.businessLicense,
      };
      const oss = map[type];
      if (!oss) {
        this.$message &&
          this.$message.warning &&
          this.$message.warning("暂无附件");
        return;
      }
      const url = `${this.baseUrl}admin/file/get?ossFilePath=${oss}`;
      window.open(url, "_blank");
    },

    openFile(file) {
      const url = file?.imgUrl
        ? `/api/admin/file/get?ossFilePath=${file.imgUrl}`
        : null;
      if (url) {
        window.open(url, "_blank");
      } else {
        this.$message &&
          this.$message.info &&
          this.$message.info("无法打开附件");
      }
    },
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
}

.header {
  background: none;
  color: #34495e;
  text-align: left;
  padding: 20px 0 20px 10px;
  font-size: 24px;
  font-weight: bold;
}

.main-content {
  background-color: #ffffff;
  margin: 20px 40px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 40px;
  min-height: calc(100vh - 160px);
  animation: fadeIn 0.5s ease-in;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  color: #34495e;
  margin-bottom: 10px;
}

.section-divider {
  border: none;
  border-top: 1px solid #ecf0f1;
  margin-bottom: 20px;
}

.info-box {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.product-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 10px;
}

.info-item {
  margin-bottom: 15px;
}
.label {
  font-size: 14px;
  color: #2c3e50;
  display: inline-block;
  min-width: 100px;
}
.value {
  font-size: 14px;
  color: #2c3e50;
  font-weight: bold;
}

.applicant-info {
  margin-bottom: 20px;
}
.contact-info {
  margin-bottom: 15px;
  color: #2c3e50;
}

.attachment-container {
  display: flex;
  gap: 20px;
  margin: 15px 0;
  align-items: center;
  flex-wrap: wrap;
}

.attachment-preview {
  width: 120px;
  height: 80px;
  background-color: #e9ecef;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  color: #7f8c8d;
  transition: all 0.3s ease;
}
.attachment-preview:hover {
  background-color: #dee2e6;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.attachment-thumb {
  width: 120px;
  height: 80px;
  border-radius: 4px;
}
.image-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #e9ecef;
  color: #7f8c8d;
}

.loan-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 10px;
}
.detail-item {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.purpose-item {
  grid-column: 1 / -1;
  margin-top: 10px;
}

.material-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
}
.material-item {
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f4;
}
.material-item:last-child {
  border-bottom: none;
}
.material-name {
  color: #2c3e50;
  font-size: 14px;
}
.material-name.clickable {
  color: #3498db;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 8px 12px;
  border-radius: 4px;
  display: inline-block;
}
.material-name.clickable:hover {
  background-color: #e8f4fd;
  color: #2980b9;
  transform: translateX(5px);
}

.material-thumb {
  width: 100px;
  height: 100px;
  border-radius: 4px;
}

.audit-result {
  margin-top: 20px;
}
.audit-status {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-bottom: 15px;
}
.status-approved {
  color: #28a745;
  font-weight: bold;
}
.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.status-icon {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #28a745;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
}

.bottom-notice {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 20px;
  margin-top: 30px;
}
.notice-title {
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 5px;
}
.notice-content {
  font-size: 12px;
  color: #7f8c8d;
}

.bottom-actions {
  margin-top: 20px;
  text-align: right;
}
.bottom-actions .el-button {
  padding: 12px 24px;
  font-size: 16px;
  border-radius: 8px;
  min-width: 160px;
}

@media (max-width: 768px) {
  .main-content {
    margin: 10px 20px;
    padding: 20px;
  }
  .header {
    padding: 15px 30px;
    font-size: 20px;
  }
  .product-info {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .loan-details {
    grid-template-columns: 1fr;
  }
  .bottom-actions {
    text-align: center;
  }
  .bottom-actions .el-button {
    width: 100%;
    max-width: none;
  }
  .audit-status {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .attachment-container {
    flex-direction: column;
    align-items: flex-start;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 旧需求中的可点击 td 样式 */
.clickable-td {
  cursor: pointer;
  font-weight: bold;
  padding: 8px 12px;
  border-radius: 4px;
  transition: all 0.3s ease;
  user-select: none;
  color: #409eff;
}
.clickable-td:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.clickable-td:active {
  transform: translateY(0);
}
</style>
